import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAVIGATION_GROUPS, ROUTE_PATHS } from '../config/navigation';

const getComparablePath = (path) => {
  const [pathname] = path.split('?');
  return pathname;
};

const Sidebar = () => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const userRole = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}')?.role || 'employee';
    } catch (error) {
      return 'employee';
    }
  }, []);
  const activeCompanyId = useMemo(() => localStorage.getItem('activeCompanyId') || '', []);

  const visibleNavigationGroups = useMemo(
    () =>
      NAVIGATION_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const roleAllowed = !item.roles || item.roles.includes(userRole);

          if (!roleAllowed) {
            return false;
          }

          if (userRole === 'super_admin' && item.requiresCompanyScope && !activeCompanyId) {
            return false;
          }

          return true;
        })
      })).filter((group) => group.items.length > 0),
    [activeCompanyId, userRole]
  );

  const isActive = (path) => `${location.pathname}${location.search}` === path;
  const isPathInCurrentSection = useCallback(
    (path) => getComparablePath(path) === location.pathname,
    [location.pathname]
  );

  const activeGroupKey = useMemo(() => {
    const activeGroup = visibleNavigationGroups.find((group) =>
      group.items.some((item) => !item.external && isPathInCurrentSection(item.path))
    );

    return activeGroup?.key || null;
  }, [isPathInCurrentSection, visibleNavigationGroups]);

  useEffect(() => {
    if (!activeGroupKey) {
      return;
    }

    setExpandedMenus((prev) => ({
      ...prev,
      [activeGroupKey]: true
    }));
  }, [activeGroupKey]);

  const toggleSubmenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Sidebar daraltma/genişletme — body sınıfıyla CSS değişkenini değiştirir.
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    localStorage.setItem('sidebarCollapsed', String(collapsed));
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [collapsed]);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  return (
    <div className="sidebar modern-sidebar">
      <div className="sidebar-logo modern-sidebar-logo">
        <button
          type="button"
          className="sidebar-close modern-sidebar-close"
          id="sidebar-close"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
          aria-pressed={collapsed}
        >
          <i className="bx bx-left-arrow-alt"></i>
        </button>
      </div>

      <div className="simlebar-sc" data-simplebar>
        <ul className="sidebar-menu tf">
          <li className="modern-sidebar-item">
            <Link
              to={ROUTE_PATHS.dashboard}
              className={`modern-sidebar-link ${isActive(ROUTE_PATHS.dashboard) ? 'active' : ''}`}
            >
              <i className="bx bxs-home modern-sidebar-icon"></i>
              <span>Anasayfa</span>
            </Link>
          </li>

          {visibleNavigationGroups.map((group) => {
            const isGroupActive = group.items.some(
              (item) => !item.external && isPathInCurrentSection(item.path)
            );

            if (group.path) {
              return (
                <li key={group.key} className="modern-sidebar-item">
                  <Link
                    to={group.path}
                    className={`modern-sidebar-link ${isPathInCurrentSection(group.path) ? 'active' : ''}`}
                  >
                    <i className={`${group.icon} modern-sidebar-icon`}></i>
                    <span>{group.label}</span>
                  </Link>
                </li>
              );
            }

            // Tek öğeli grup: açılır menü yerine doğrudan tıklanabilir tek bağlantı.
            if (group.items.length === 1) {
              const [singleItem] = group.items;

              return (
                <li key={group.key} className="modern-sidebar-item">
                  {singleItem.external ? (
                    <a
                      href={singleItem.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modern-sidebar-link"
                    >
                      <i className={`${group.icon} modern-sidebar-icon`}></i>
                      <span>{group.label}</span>
                    </a>
                  ) : (
                    <Link
                      to={singleItem.path}
                      className={`modern-sidebar-link ${isActive(singleItem.path) ? 'active' : ''}`}
                    >
                      <i className={`${group.icon} modern-sidebar-icon`}></i>
                      <span>{group.label}</span>
                    </Link>
                  )}
                </li>
              );
            }

            return (
              <li key={group.key} className="sidebar-submenu modern-sidebar-item">
                <button
                  className={`modern-sidebar-dropdown ${
                    expandedMenus[group.key] || isGroupActive ? 'active' : ''
                  }`}
                  onClick={() => toggleSubmenu(group.key)}
                >
                  <div className="modern-sidebar-dropdown-content">
                    <i className={`${group.icon} modern-sidebar-icon`}></i>
                    <span>{group.label}</span>
                  </div>
                  <div className="modern-sidebar-dropdown-icon">
                    <i className={`bx bx-chevron-down ${expandedMenus[group.key] ? 'active' : ''}`}></i>
                  </div>
                </button>

                <ul className={`modern-sidebar-dropdown-menu ${expandedMenus[group.key] ? 'active' : ''}`}>
                  {group.items.map((item) => (
                    <li key={item.key} className="modern-sidebar-dropdown-item">
                      {item.external ? (
                        <a
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="modern-sidebar-dropdown-link"
                        >
                          <span>{item.label}</span>
                          {item.meta && <small className="ms-2 text-muted">{item.meta}</small>}
                        </a>
                      ) : (
                        <Link
                          to={item.path}
                          className={`modern-sidebar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                        >
                          <span>{item.label}</span>
                          {item.meta && <small className="ms-2 text-muted">{item.meta}</small>}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
